/* System defined core library */
import { ChangeDetectorRef, Component, ElementRef, HostListener, OnInit, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';

/* User defined library/components */
import { BootstrapParams } from '../../../../bootstrap-params';
import { DemographicsService } from '../../../shared/services/demographics.service';
import { DynamicComponentService } from '../../../shared/services/dynamic.component.service';
import { RoutingService } from '../../../shared/services/routing.service';
import { TimerService } from '../../../shared/services/timer.service';
import { UtilityService } from '../../../shared/services/utility.service';
import { OrchestrationApiService } from '../../shared/services/orchestration-api.service';
import { SchedulingApiService } from '../../shared/services/scheduling-api.service';
import { SelectedExamDetailService } from '../../shared/services/selected-exam-detail.service';

/* User defined models */
import { AppointmentDetail } from '../../../shared/models/scheduling/appointmentdetail.model';
import { AppointmentSlot } from '../../../shared/models/scheduling/appointmentSlot.model';
import { Candidate } from '../../../shared/models/scheduling/candidate.model';
import { Client } from '../../../shared/models/scheduling/client.model';
import { Hold } from '../../../shared/models/scheduling/hold.model';
import { Payment } from '../../../shared/models/scheduling/payment.model';
import { Proctor } from '../../../shared/models/scheduling/proctor.model';
import { Program } from '../../../shared/models/scheduling/program.model';
import { SiteSlot } from '../../../shared/models/scheduling/siteSlot.model';
import { Timezone } from '../../../shared/models/scheduling/timezone.model';
import { Exam } from '../../../shared/models/scheduling/exam.model';

/* User defined enums/constants */
import { ComponentType, Fee } from '../../../shared/enums/scheduling.enum';
import { ProctorSelectorComponent } from '../../shared/components/proctor-selector/proctor-selector.component';


@Component({
  selector: 'app-schedule-proctor',
  templateUrl: './schedule-proctor.component.html',
  styleUrls: ['./schedule-proctor.component.scss'],
  providers: [DynamicComponentService]
})
/** schedule-proctor component*/
export class ScheduleProctorComponent implements OnInit {
  @HostListener('window:popstate', ['$event'])
  @ViewChild(ProctorSelectorComponent) proctorSelectorInstance: ProctorSelectorComponent;
  @ViewChild('refreshavailabilitybtn') refreshBtn: ElementRef;

  onPopState(event: any) {
    this.clearDataOnGoingBack();
  }

  /* User defined private/public variables */
  candidate: Candidate;
  client: Client;
  isSlotSelected: boolean;
  IsHoldInProgress: boolean;
  hasErrorOccured: boolean;
  holdSlotObj: any;
  isSchedulePaymentTrue: boolean = true;
  isNextButtonVisible: boolean;
  prevSelectedHoldObj: any = [];
  prevSelectedHoldObjArr: any = [];
  program: Program;
  remoteDetail: AppointmentDetail[] = [];
  selectedAppointments: AppointmentDetail[] = [];
  selectedAppointmentData: AppointmentDetail[] = [];
  selectedSite: SiteSlot;
  selectedIndex: number = 0;
  selectedProctor: Proctor;
  timezone: Timezone;
  hasConflictErrorOccured: boolean;
  examWithTA: Exam[] = []

  /* Service subscriptions */
  candidate$: Subscription;
  client$: Subscription;
  prevSelectedHoldObj$: Subscription;
  program$: Subscription;
  selectedAppointments$: Subscription;
  selectedSite$: Subscription;
  selectedProctor$: Subscription;
  timezone$: Subscription;

  /* Constructor declaration with required injected services */
  constructor(
    private bootstrapParams: BootstrapParams,
    private demographicsService: DemographicsService,
    private dynamicComponentService: DynamicComponentService,
    private orchestrationApiService: OrchestrationApiService,
    private routingService: RoutingService,
    private schedulingApiService: SchedulingApiService,
    private selectedExamDetailService: SelectedExamDetailService,
    private timerService: TimerService,
    private utilityService: UtilityService,
    private cdRef: ChangeDetectorRef
  ) {
    this.IsHoldInProgress = false;
    this.isSlotSelected = false;

    this.selectedAppointments$ = selectedExamDetailService.selectedAppointmentDetails.subscribe(
      selectedAppointments => {
        this.selectedAppointmentData = selectedAppointments;
      });

    this.selectedSite$ = this.selectedExamDetailService.siteSlot.subscribe(
      siteSlot => {
        this.selectedSite = siteSlot;
      });

    this.program$ = this.selectedExamDetailService.program.subscribe(
      program => {
        this.program = program;
      });

    this.client$ = this.selectedExamDetailService.client.subscribe(
      client => {
        this.client = client;
      });

    this.prevSelectedHoldObj$ = selectedExamDetailService.prevSelectedHoldObj.subscribe(
      prevSelectedHoldObj => {
        this.prevSelectedHoldObj = prevSelectedHoldObj;
        if (this.prevSelectedHoldObj) {
          this.prevSelectedHoldObjArr = prevSelectedHoldObj;
        }
      });

    this.selectedProctor$ = selectedExamDetailService.slot.subscribe(
      slot => {
        this.selectedProctor = slot;
      });

    this.selectedIndex = this.selectedExamDetailService.selectedAppointmentIndex;

    this.timezone$ = selectedExamDetailService.timezone.subscribe(
      timezone => {
        this.timezone = timezone;
      });

    this.candidate$ = this.selectedExamDetailService.candidate.subscribe(
      candidate => {
        this.candidate = candidate;
      });
  }

  /* Initialize the component & call required methods when component loaded first time. */
  ngOnInit() {
    sessionStorage.setItem('previousUrl', window.location.pathname);
    if (this.selectedProctor) {
      this.isSlotSelected = true;
    }

    this.selectedAppointmentData.forEach((appointment) => {
      let tempExamWithTA: any = { id: appointment.exam.id, testingAccommodations: appointment.exam.testingAccommodations }
      this.examWithTA.push(tempExamWithTA);
    });

    if (!this.utilityService.isDataValidToLoadComponent(ComponentType.ScheduleProctorComponent)) return;
  }

  /* Move back a page. */
  goToPrevPage(): void {
    this.clearDataOnGoingBack();
    this.routingService.backwardNavigation(ComponentType.ScheduleProctorComponent);
  }

  /* Use to clear details on navigating backward */
  clearDataOnGoingBack(): void {
    this.selectedExamDetailService.setProctors(null);
    this.selectedExamDetailService.setSlot(null);
    this.selectedExamDetailService.setSelectedStartDate(null);
    this.selectedExamDetailService.setSelectedEndDate(null);
    this.selectedExamDetailService.setHold(null);
    this.timerService.clearClock();

    this.selectedExamDetailService.scheduleFee = {
      totalAmount: 0,
      currency: null
    };

    this.selectedAppointmentData.forEach((appointment) => {
      appointment.appointmentSlot = new AppointmentSlot();
      appointment.hold = new Hold();
      appointment.holdId = undefined;
      appointment.status = undefined;
    });

    this.selectedExamDetailService.setSelectedAppointmentDetails(this.selectedAppointmentData);
  }

  /* Move to next page */
  goToNextPage() {
    this.isSlotSelected = false; // Only need to disable the next button once clicked (No hide)
    this.IsHoldInProgress = true;
    if (this.holdSlotObj) {
      this.schedulingApiService.getSlotCountry(this.selectedAppointments[this.selectedIndex].hold.slot.id).subscribe((response) => {
        this.selectedExamDetailService.proctorCountry = response.location.address.country;
        this.selectedExamDetailService.proctorSiteId = response.id;
        this.getExamPaymentDetails();
        this.holdSlot(this.holdSlotObj);
      });
    }
    else if (this.prevSelectedHoldObjArr.length > 0) {
      this.routingService.scheduleFwdNavigation(ComponentType.ScheduleProctorComponent);
    }
  }

  // TODO - move these methods to service - KJC
  getExamPaymentDetails(): void {
    let totalAmount: number = 0;
    let currencyCode: string = '';
    let isPaymentRequired: boolean;
    
    this.selectedAppointments.forEach((appointmentDetail) => {
      appointmentDetail.payments = [];
      this.schedulingApiService.getPaymentFeeDetail(appointmentDetail.exam.id, this.selectedExamDetailService.proctorCountry).subscribe((paymentFee) => {
        paymentFee.forEach(payment => {
          if (payment.type == Fee.InitialScheduleFee) {
            let tmpPayment: Payment = new Payment(
              payment.amount,
              payment.currency,
              Fee.InitialScheduleFee
            );
            totalAmount += payment.amount;
            currencyCode = payment.currency;
            appointmentDetail.payments.push(tmpPayment);
            this.selectedExamDetailService.scheduleFee = {
              totalAmount: totalAmount,
              currency: currencyCode
            };

            isPaymentRequired = appointmentDetail.exam.hasPayment && totalAmount > 0;
            this.selectedExamDetailService.isPaymentRequired = isPaymentRequired;
            this.selectedExamDetailService.setSelectedAppointmentDetails(this.selectedAppointments);

            if (!appointmentDetail.exam.hasPayment) {
              this.clearPaymentDetails(appointmentDetail.exam.id);
            }
          }
        });
      });
    });
  }

  // TODO - move these methods to service - KJC
  clearPaymentDetails(examId?: any) {
    examId = examId || this.selectedAppointments[this.selectedIndex].exam.id;
    let totalAmount = 0;
    let currencyCode = '';
    this.selectedAppointments.forEach((appointmentDetail) => {
      if (appointmentDetail.exam.id === examId) {
        appointmentDetail.payments.forEach((payment) => {
          if (payment) {
            payment.amount = 0;
          } else {
            let tmpPayment: Payment = new Payment(0);
            appointmentDetail.payments.push(tmpPayment);
          }
        });
      }
    });

    this.selectedAppointments.forEach(appt => {
      if (appt.payments.length) {
        totalAmount += appt.payments[0].amount;
        currencyCode = appt.payments[0].currency;
      }
    });

    this.selectedExamDetailService.scheduleFee = {
      totalAmount: totalAmount,
      currency: currencyCode
    };

    this.selectedExamDetailService.isPaymentRequired = this.selectedAppointments.some(appt => appt.exam.hasPayment) && totalAmount > 0;

    this.selectedExamDetailService.setSelectedAppointmentDetails(this.selectedAppointments);
  }

  // TODO - move these methods to service - KJC
  setSettingsFromFlows(response: any, examId?: string) {
    const notificationResp = response.filter(function (el: any) {
      return el.name === 'Verification';
    });

    this.selectedExamDetailService.showTextReminder = notificationResp[0].data &&
      notificationResp[0].data.length &&
      notificationResp[0].data[0].parameters.smsText === 'True' ? true : false;

    const paymentResp = response.filter(function (el: any) {
      return el.name === 'Payment';
    });

    examId = examId || this.selectedAppointments[this.selectedExamDetailService.selectedAppointmentIndex].exam.id;

    this.selectedAppointments.forEach(
      appt => {
        if (appt.exam.id === examId) {
          appt.exam.hasPayment = paymentResp.length && paymentResp[0].settings.schedule === 'True';
        }
      });

    this.selectedExamDetailService.isBiometricConsentRequiredSchedule = true;

    // To hide Biometric consent for the clients listed in the config
    if (this.bootstrapParams.hideBiometricsClients.indexOf(this.client.code.toUpperCase()) !== -1) {
      this.selectedExamDetailService.isBiometricConsentRequiredSchedule = false;
    }

    this.selectedExamDetailService.setSelectedAppointmentDetails(this.selectedAppointments);
    
    this.selectedAppointments.forEach(
      appt => {
        if (!appt.exam.hasPayment) {
          this.clearPaymentDetails(appt.exam.id);
        }
      });
  }

  /* disable next button on the basis of local variable value */
  public isDisable(): boolean {
    return !this.isSlotSelected;
  }

  public setSelectedSiteDetails(selectedSiteDetails: any): void {
    this.selectedAppointments = selectedSiteDetails.selectedAppointments;
    this.selectedIndex = selectedSiteDetails.selectedIndex;
    this.holdSlotObj = selectedSiteDetails.holdSlotObj;
    this.hasErrorOccured = false;

    this.selectedAppointments.forEach(appt => appt.exam.hasPayment = true);
  }

  public setSlotsCheck(slotsCheck: any): void {
    this.hasConflictErrorOccured = false;
    this.isSlotSelected = slotsCheck;
  }

  refreshSlotAvailability(): void {
    this.proctorSelectorInstance.getAvailableTimes();
  }

  // TODO - move these methods to service - KJC
  holdSlot(holdSlot: Hold): void {
    this.selectedIndex = this.selectedExamDetailService.selectedAppointmentIndex;

    this.schedulingApiService.holdProctor(this.selectedAppointments[this.selectedIndex].hold, this.timezone.code, this.examWithTA).subscribe((holdsObjects: any) => {
      if (holdsObjects.length > 0) {

        this.selectedAppointments.forEach((appt: AppointmentDetail, index) => {
          appt.hold = holdsObjects[index];
          appt.holdId = holdsObjects[index].id;
          holdSlot.id = holdsObjects[index].id;
          this.prevSelectedHoldObjArr.push(holdSlot);
          this.selectedAppointments[index].hold = holdsObjects[index];

          if (index == 0) {
            this.selectedAppointments[index].appointmentSlot.startDateTime = new Date(this.utilityService.getFormatToFullMMDDYYYY(appt.appointmentSlot.startDateTime, "/"));
            this.selectedExamDetailService.setHold(holdSlot);
          } else {
            this.selectedAppointments[index].appointmentSlot.testTime = this.selectedAppointments[index-1].appointmentSlot.testEndTime;
            this.selectedAppointments[index].appointmentSlot.duration = this.selectedAppointments[index].exam.duration;
            this.selectedAppointments[index].appointmentSlot.startDateTime = UtilityService.addMins(
              this.selectedAppointments[index - 1].appointmentSlot.startDateTime,
              this.selectedAppointments[index - 1].appointmentSlot.duration
            );
            this.selectedAppointments[index].appointmentSlot.testEndTime = this.utilityService.getStartEndFormat(
              UtilityService.addMins(
                this.selectedAppointments[index].appointmentSlot.startDateTime,
                this.selectedAppointments[index].appointmentSlot.duration
              )
            );
          }
        });

        this.selectedExamDetailService.setprevSelectedHoldObj(this.prevSelectedHoldObjArr);

        this.selectedAppointments[this.selectedIndex].appointmentSlot.id = this.holdSlotObj.slot.id;
        this.selectedAppointments[this.selectedIndex].appointmentSlot.href = this.holdSlotObj.slot.uri;
        this.selectedAppointments[this.selectedIndex].status = 'schedule'; //added to show 'Total Duration' on Verification & Comfirmation page
        this.selectedExamDetailService.SetSlotDataSource(this.selectedAppointments[this.selectedIndex].appointmentSlot);
        this.selectedExamDetailService.isRescheduleSlotSelected = true;

        this.selectedExamDetailService.setSelectedAppointmentDetails(this.selectedAppointments);

        if (this.selectedExamDetailService.isBackToBackRequired) {
          this.orchestrationApiService.getExamSetFlows(
            this.client.code,
            this.program.code,
            this.selectedExamDetailService.examSetId,
            this.selectedExamDetailService.proctorCountry
          ).subscribe((resp: any[]) => {
            let examsOutOfSet = 0;
            this.selectedAppointments.forEach(appointment => {
              if (!resp.find((flow) => { return flow.id === appointment.exam.id })) {
                examsOutOfSet++;
                this.setSettingsFromFlows(resp, appointment.exam.id);
                this.selectedExamDetailService.canSkipProfile = this.demographicsService.canSkipDemographicPage(resp, this.candidate);
                if (this.selectedExamDetailService.canSkipProfile) {
                  this.dynamicComponentService.setCandidateProfileforSkip(resp);
                }
                this.routingService.scheduleFwdNavigation(ComponentType.ScheduleProctorComponent);
              }
            });
            resp.forEach((flow) => {
              this.setSettingsFromFlows(flow.flowSet, flow.id);
              if (!examsOutOfSet) {
                this.selectedExamDetailService.canSkipProfile = this.demographicsService.canSkipDemographicPage(resp, this.candidate);
                if (this.selectedExamDetailService.canSkipProfile) {
                  this.dynamicComponentService.setCandidateProfileforSkip(resp);
                }
                this.routingService.scheduleFwdNavigation(ComponentType.ScheduleProctorComponent);
              }
            });
          })
        } else {
          this.orchestrationApiService.getFlows(
            this.client.code,
            this.program.code,
            this.selectedAppointments[this.selectedIndex].exam.code,
            this.selectedExamDetailService.proctorCountry
          ).subscribe((resp) => {
            this.setSettingsFromFlows(resp);
            this.selectedExamDetailService.canSkipProfile = this.demographicsService.canSkipDemographicPage(resp, this.candidate);
            if (this.selectedExamDetailService.canSkipProfile) {
              this.dynamicComponentService.setCandidateProfileforSkip(resp);
            }
            this.routingService.scheduleFwdNavigation(ComponentType.ScheduleProctorComponent);
          });
        }
      }
      else {
        if(holdsObjects == -2)
        {
            this.hasErrorOccured = true;
            this.hasConflictErrorOccured = true;
            this.cdRef.detectChanges();
            this.isNextButtonVisible = true;
            this.isSlotSelected = false;
            this.refreshBtn.nativeElement.focus();
        }
        else {
            this.hasErrorOccured = true;
            this.isNextButtonVisible = true;
            this.isSlotSelected = false;
        }
      }
    });
  }
}
